import { taxConfigsToRegionRates, type TaxRatesByRegion } from "@bcc/lib/products/cross-border-tax";
import { getTaxConfigurations } from "@client-shared/services/api/taxConfig";
import type { CrossBorderCountries } from "@shared/schemas/enums/crossBorder.enum";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const TAX_RATES_QUERY_KEY = "taxRates";

const STALE_TIME_MS = 5 * 60_000;

interface TaxRatesResult {
  readonly ratesByRegion: TaxRatesByRegion;
  readonly isTaxRatesLoading: boolean;
}

/**
 * Resolves per-region VAT/KS rates for cross-border pricing from the Tax Rate
 * API, keyed by the vendor's region. Disabled when the vendor has no
 * cross-border ship-to countries. On any failure (or before the Tax Rate API lands) the
 * mapper returns `DEFAULT_TAX_RATES_BY_REGION`, so callers always get usable
 * rates without special-casing the loading/error states.
 */
export function useTaxRates(vendorRegion: string | undefined, shipTo: CrossBorderCountries[]): TaxRatesResult {
  const enabled = Boolean(vendorRegion) && shipTo.length > 0;
  const shipToKey = useMemo(() => [...shipTo].sort((a, b) => a.localeCompare(b)).join(","), [shipTo]);

  const { data, isLoading } = useQuery({
    queryKey: [TAX_RATES_QUERY_KEY, vendorRegion, shipToKey],
    queryFn: () => {
      if (!vendorRegion) throw new Error("vendorRegion is required");
      return getTaxConfigurations(vendorRegion);
    },
    enabled,
    staleTime: STALE_TIME_MS,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const ratesByRegion = useMemo(() => taxConfigsToRegionRates(data), [data]);

  return { ratesByRegion, isTaxRatesLoading: enabled && isLoading };
}
